import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import {
  Page,
  LayoutSingleColumn,
  IconSpinner,
  StrapiContentRenderer,
  NamedLink,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import facebookImage from '../../assets/biketribe-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/biketribe-twitter-sharing-600x314.jpg';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';
import FooterContainer from '../FooterContainer/FooterContainer';
import { getImagesLink } from '../../util/dataExtractors';
import { convertMarkdownToReact } from '../../util/markdownHelpers';
import {
  fetchSingleBlog,
  getBlogSelector,
  getBlogsInProgress,
  getBlogsTagsSelector,
} from '../../ducks/strapi.duck';
import { isArrayLength } from '../../util/genericHelpers';

import css from './SingleBlogPage.module.css';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routing/routeConfiguration';

const baseUrl = process.env.REACT_APP_STRAPI_BASE_URL;

export const SingleBlogPageComponent = props => {
  const { scrollingDisabled, params, history } = props;

  const config = useConfiguration();
  const dispatch = useDispatch();

  const slug = params?.title;

  useEffect(() => {
    dispatch(fetchSingleBlog({ slug }));
  }, [slug]);

  const blogData = useSelector(getBlogSelector);
  const blogsInProgress = useSelector(getBlogsInProgress);
  const blogsTags = blogData?.attributes?.tags?.data || [];

  const blogImages = blogData?.attributes?.coverImage?.data;
  const title = blogData?.attributes?.title;
  const description = blogData?.attributes?.description;
  const imagesLink = getImagesLink([blogImages], baseUrl);

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={description}
      title={title}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: description,
        name: title,
        image: [facebookImage],
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.pageContent}>
          <div className={css.blogsContainer}>
            {blogsInProgress ? (
              <div className={css.searchInProgress}>
                <IconSpinner />
              </div>
            ) : (
              <>
                <div className={css.listingBreadcrumbs}>
                  <div className={css.contentWidth}>
                    <div className={css.breadcrumbs}>
                      {/* <NamedLink name="LandingPage">Home</NamedLink> */}
                      <NamedLink name="BlogPage">OnBuoy Blog</NamedLink>
                      <span className={css.current}>{title}</span>
                    </div>
                    <div className={css.listingActions} />
                  </div>
                </div>
                <div className={css.blogsContent}>
                  <h1>{title}</h1>
                  {isArrayLength(imagesLink) && <img src={imagesLink[0]} alt={title} />}
                  {description ? <StrapiContentRenderer content={description} /> : null}
                </div>
              </>
            )}
            <div className={css.tagsContainer}>
              {isArrayLength(blogsTags) && !blogsInProgress
                ? blogsTags.map((tag, i) => (
                    <span
                      role="button"
                      key={i}
                      className={css.blogTag}
                      onClick={() => {
                        history.push(
                          createResourceLocatorString(
                            'BlogPage',
                            routeConfiguration(),
                            {},
                            { tag: tag?.attributes?.slug }
                          )
                        );
                      }}
                    >
                      {tag?.attributes?.name}
                    </span>
                  ))
                : null}
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

SingleBlogPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

const SingleBlogPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl
)(SingleBlogPageComponent);

export default SingleBlogPage;
